import { useEffect, type RefObject } from 'react';

/**
 * Calls `onClickOutside` when a mousedown event occurs outside all provided refs.
 *
 * event.target is typed as EventTarget | null but Node.contains() requires Node.
 * In DOM mouse events the target is always a Node; the cast bridges the TS DOM lib gap.
 */
export function useClickOutside(
  refs: ReadonlyArray<RefObject<HTMLElement | null>>,
  onClickOutside: () => void,
  deps: ReadonlyArray<unknown> = [],
): void {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node;
      const isInside = refs.some(
        (ref) => ref.current && ref.current.contains(target),
      );
      if (!isInside) {
        onClickOutside();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [...refs, onClickOutside, ...deps]);
}
